// ClienteForm.jsx - Sistema de 2 etapas con verificación de duplicados
import React, { useState, useEffect } from "react";
import { formatearMoneda } from '../utils/moneda';
import Swal from 'sweetalert2';
import { solicitarFacturacionAnteojo } from '../utils/facturacionBridge';
import './ClienteForm.css';
import CristalSearchInput from '../components/CristalSearchInput';
import ArmazonSearchInput from '../components/ArmazonSearchInput';
import EscanearFicha from '../components/EscanearFicha2';
import { 
  obtenerUltimoNroSobre, 
  calcularSaldo, 
  crearCliente, 
  agregarAnteojo,
  verificarClienteDuplicadoAvanzado,
  obtenerClienteCompleto,
  obtenerArmazonesDisponibles,
  obtenerCristalesPorTipo
} from "../api/backend_v2";
import {
  calcularPrecioEfectivoConDescuento,
  cargarDescuentoEfectivoSeguro,
  cargarDescuentoArmazonesSeguro,
  DESCUENTO_EFECTIVO_DEFAULT,
  DESCUENTO_ARMAZONES_DEFAULT,
  resolverCondicionesArmazon,
  esArmazonCacic
} from "../utils/precios";
import { obtenerProveedores } from "../utils/proveedores";
import { isDemoUser, solicitarRegistroDemo } from "../utils/demoMode";
import { authService } from "../api/auth";

// ✅ Mover el componente CollapsibleSection FUERA del componente principal
const CollapsibleSection = React.memo(({ title, isExpanded, onToggle, children }) => {
  return (
    <div className="collapsible-section">
      <div 
        className={`collapsible-header ${isExpanded ? 'expanded' : ''}`}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span className="chevron">▼</span>
      </div>
      <div className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}>
        {isExpanded && children}
      </div>
    </div>
  );
});

const construirObservacionBeneficio = (tipoBeneficio, nroSobre) => {
  if (tipoBeneficio !== 'seguro' && tipoBeneficio !== 'promocion') {
    return "";
  }

  const etiqueta = tipoBeneficio === 'seguro' ? 'seguro' : 'promocion';
  const sobre = nroSobre ? ` del sobre #${nroSobre}` : "";
  return `Lente por ${etiqueta}${sobre}`;
};

const combinarObservaciones = (observacionActual, observacionAutomatica) => {
  const actual = String(observacionActual || '').trim();
  const automatica = String(observacionAutomatica || '').trim();

  if (!automatica) {
    return actual;
  }

  if (!actual) {
    return automatica;
  }

  const actualNormalizado = actual.toLowerCase();
  const automaticaNormalizada = automatica.toLowerCase();
  if (actualNormalizado.includes(automaticaNormalizada)) {
    return actual;
  }

  return `${actual}. ${automatica}`;
};

const ClienteForm = ({ modoAgregarAnteojo = false, clienteExistente = null, ultimoAnteojo: ultimoAnteojoProp = null, origenBeneficio = null, onClose, onViewChange }) => {
  // Estados para el formulario de 2 etapas
  const [etapaActual, setEtapaActual] = useState(modoAgregarAnteojo ? 2 : 1);
  
  const [clienteCreado, setClienteCreado] = useState(clienteExistente || null);
  const [clienteDuplicado, setClienteDuplicado] = useState(null);
  const [mostrarAdvertenciaDuplicado, setMostrarAdvertenciaDuplicado] = useState(false);
  const [mostrarModalReplicar, setMostrarModalReplicar] = useState(false);
  const [mostrarEscanerFicha, setMostrarEscanerFicha] = useState(false);
  const beneficioForzado = origenBeneficio === 'seguro' || origenBeneficio === 'promocion' ? origenBeneficio : null;
  
  // Datos del cliente (etapa 1)
  const [datosCliente, setDatosCliente] = useState(clienteExistente ? {
    nombre: clienteExistente.nombre,
    apellido: clienteExistente.apellido,
    telefono: clienteExistente.telefono,
    padre_madre_encargado: clienteExistente.padre_madre_encargado || "",
    domicilio: clienteExistente.domicilio || "",
    email: clienteExistente.email || "",
    obra_social: clienteExistente.obra_social || "",
    observaciones: clienteExistente.observaciones || "",
    fecha: clienteExistente.fecha_registro || new Date().toISOString().split('T')[0],
  } : {
    nombre: "",
    apellido: "",
    telefono: "",
    padre_madre_encargado: "",
    domicilio: "",
    email: "",
    obra_social: "",
    observaciones: "",
    fecha: new Date().toISOString().split('T')[0],
  });

  // Datos del anteojo (etapa 2)  
  const [datosAnteojo, setDatosAnteojo] = useState({
    nro_sobre: "",
    lejos_esf_od: "",
    lejos_cil_od: "",
    lejos_eje_od: "",
    lejos_esf_oi: "",
    lejos_cil_oi: "",
    lejos_eje_oi: "",
    lejos_cristal: "",
    lejos_armazon: "",
    lejos_armazon_id: null,
    cerca_esf_od: "",
    cerca_cil_od: "",
    cerca_eje_od: "",
    cerca_esf_oi: "",
    cerca_cil_oi: "",
    cerca_eje_oi: "",
    cerca_cristal: "",
    cerca_armazon: "",
    cerca_armazon_id: null,
    altura_od: "",
    altura_oi: "",
    dnp_od: "",
    dnp_oi: "",
    grados_prisma_od: "",
    grados_prisma_oi: "",
    base_od: "",
    base_oi: "",
    observaciones: "",
    precio_total: "",
    sena: "",
    tipo_pago: "efectivo",
    tipo_anteojo: "Monofocal",
    od_adicion: "",
    oi_adicion: "",
    usa_seguro: false,
    usa_promocion: false
  });

  const [ultimoAnteojo, setUltimoAnteojo] = useState(ultimoAnteojoProp || null);
  
  // Configuración comercial de óptica y proveedores para resolver precios efectivos
  const [descuentoOptica, setDescuentoOptica] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [descuentoArmazon, setDescuentoArmazon] = useState(DESCUENTO_ARMAZONES_DEFAULT);
  const [proveedores, setProveedores] = useState([]);

  // Estados para los cristales seleccionados
  const [cristalLejos, setCristalLejos] = useState(null);
  const [cristalCerca, setCristalCerca] = useState(null);

  // Estados para los armazones seleccionados
  const [armazonLejos, setArmazonLejos] = useState(null);
  const [armazonCerca, setArmazonCerca] = useState(null);
  
  // Estados para los precios de cristales y armazones
  const [preciosCristal, setPreciosCristal] = useState({
    lejos: 0,
    cerca: 0
  });
  const [preciosArmazon, setPreciosArmazon] = useState({
    lejos: 0,
    cerca: 0
  });
    
  // Estados para secciones plegables
  const [lejosExpanded, setLejosExpanded] = useState(true);
  const [cercaExpanded, setCercaExpanded] = useState(false);
  const [otrasMedidasExpanded, setOtrasMedidasExpanded] = useState(false);
  const [clienteInfoExpanded, setClienteInfoExpanded] = useState(false);

  // Cargar configuración de descuentos y proveedores al montar
  useEffect(() => {
    let montado = true;
    Promise.all([
      cargarDescuentoEfectivoSeguro(),
      cargarDescuentoArmazonesSeguro(),
      obtenerProveedores().catch(() => [])
    ]).then(([descOptica, descArmazon, provs]) => {
      if (!montado) return;
      setDescuentoOptica(descOptica);
      setDescuentoArmazon(descArmazon);
      setProveedores(provs || []);
    });
    return () => {
      montado = false;
    };
  }, []);
  
  // Si es modo agregar anteojo y hay último anteojo, mostrar modal replicar al abrir
  useEffect(() => {
    if (modoAgregarAnteojo && ultimoAnteojoProp) {
      setUltimoAnteojo(ultimoAnteojoProp);
      setMostrarModalReplicar(true);
    }
  }, [modoAgregarAnteojo, ultimoAnteojoProp]);

  useEffect(() => {
    if (!beneficioForzado) return;

    const observacionAutomatica = construirObservacionBeneficio(beneficioForzado, ultimoAnteojoProp?.nro_sobre);
    setDatosAnteojo((prev) => {
      const observacionesCombinadas = combinarObservaciones(prev.observaciones, observacionAutomatica);
      return {
        ...prev,
        usa_seguro: beneficioForzado === 'seguro',
        usa_promocion: beneficioForzado === 'promocion',
        observaciones: observacionesCombinadas,
      };
    });
  }, [beneficioForzado, ultimoAnteojoProp]);

  // Bloquear scroll del body cuando el modal de replicar está abierto
  useEffect(() => {
    if (mostrarModalReplicar) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = originalOverflow; };
    }
  }, [mostrarModalReplicar]);

  const toNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') return 0;
    const normalized = value.replace(',', '.').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const resolverPrecioCristal = React.useCallback((cristal, esEfectivo) => {
    if (!cristal) return 0;
    const precioPublico = toNumber(cristal.precio_publico);
    if (!esEfectivo) {
      return precioPublico;
    }
    // Si la forma de pago es efectivo o transferencia, usar valor efectivo
    if (cristal.precio_efectivo != null && !isNaN(Number(cristal.precio_efectivo)) && Number(cristal.precio_efectivo) > 0) {
      return toNumber(cristal.precio_efectivo);
    }
    if (precioPublico > 0) {
      return Number(calcularPrecioEfectivoConDescuento(precioPublico, descuentoOptica)) || precioPublico;
    }
    return 0;
  }, [descuentoOptica]);

  const resolverPrecioArmazon = React.useCallback((armazon, esEfectivo) => {
    if (!armazon) return 0;
    const precioPublico = toNumber(armazon.precio_publico);
    if (!esEfectivo) {
      return precioPublico;
    }
    // Si la forma de pago es efectivo o transferencia, usar valor efectivo
    if (armazon.precio_efectivo != null && !isNaN(Number(armazon.precio_efectivo)) && Number(armazon.precio_efectivo) > 0) {
      return toNumber(armazon.precio_efectivo);
    }
    if (precioPublico > 0) {
      const cond = resolverCondicionesArmazon(armazon, proveedores, descuentoArmazon);
      const desc = cond.tieneCondicionEspecial
        ? cond.descuento
        : (esArmazonCacic(armazon) ? descuentoArmazon : (descuentoArmazon || descuentoOptica));
      return Number(calcularPrecioEfectivoConDescuento(precioPublico, desc)) || precioPublico;
    }
    return 0;
  }, [proveedores, descuentoArmazon, descuentoOptica]);

  // Sincronizar precios de cristales y armazones según la forma de pago seleccionada
  useEffect(() => {
    const esEfectivo = datosAnteojo.tipo_pago === 'efectivo' || datosAnteojo.tipo_pago === 'transferencia';

    setPreciosCristal({
      lejos: resolverPrecioCristal(cristalLejos, esEfectivo),
      cerca: resolverPrecioCristal(cristalCerca, esEfectivo)
    });

    setPreciosArmazon({
      lejos: resolverPrecioArmazon(armazonLejos, esEfectivo),
      cerca: resolverPrecioArmazon(armazonCerca, esEfectivo)
    });
  }, [
    cristalLejos,
    cristalCerca,
    armazonLejos,
    armazonCerca,
    datosAnteojo.tipo_pago,
    resolverPrecioCristal,
    resolverPrecioArmazon
  ]);

  // Calcular precio total automáticamente cuando cambien cristales y armazones
  useEffect(() => {
    const precioLejosTotal = toNumber(preciosCristal.lejos) + toNumber(preciosArmazon.lejos);
    const precioCercaTotal = toNumber(preciosCristal.cerca) + toNumber(preciosArmazon.cerca);
    
    // Determinar qué usar:
    // - Si solo tiene lejos: usar solo lejos
    // - Si solo tiene cerca: usar solo cerca
    // - Si tiene ambos: sumar ambos (anteojos bifocal/multifocal)
    let precioFinal = 0;
    
    if (precioLejosTotal > 0 && precioCercaTotal > 0) {
      // Tiene ambos: sumarlos
      precioFinal = precioLejosTotal + precioCercaTotal;
    } else {
      // Solo tiene uno: usar el que tenga valor
      precioFinal = Math.max(precioLejosTotal, precioCercaTotal);
    }
    
    if (precioFinal > 0) {
      setDatosAnteojo(prev => ({
        ...prev,
        precio_total: precioFinal.toString()
      }));
    } else if (!cristalLejos && !cristalCerca && !armazonLejos && !armazonCerca) {
      setDatosAnteojo(prev => ({
        ...prev,
        precio_total: ""
      }));
    }
  }, [preciosCristal, preciosArmazon, cristalLejos, cristalCerca, armazonLejos, armazonCerca]);

  // Función para obtener el próximo número de sobre
  const obtenerProximoNumeroSobre = async () => {
    try {
      const data = await obtenerUltimoNroSobre();
      return (data.ultimo_numero_sobre || 0) + 1;
    } catch (error) {
      console.error("Error al obtener próximo número:", error);
      return 1;
    }
  };

  // Manejar cambios en datos del cliente (etapa 1)
  const handleClienteChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setDatosCliente(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Manejar cambios en datos del anteojo (etapa 2)
  const handleAnteojoChange = React.useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setDatosAnteojo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  // Manejar confirmación para usa_seguro
  const handleUsaSeguroChange = async (e) => {
    const { checked } = e.target;
    
    if (checked) {
      const result = await Swal.fire({
        title: '¿Confirmar Seguro?',
        text: 'Este cristal va por seguro. ¿Desea activar esta opción?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, Activar Seguro',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });

      if (result.isConfirmed) {
        setDatosAnteojo(prev => ({
          ...prev,
          usa_seguro: true
        }));
      }
    } else {
      setDatosAnteojo(prev => ({
        ...prev,
        usa_seguro: false
      }));
    }
  };

  // Manejar confirmación para usa_promocion
  const handleUsaPromocionChange = async (e) => {
    const { checked } = e.target;
    
    if (checked) {
      const result = await Swal.fire({
        title: '¿Confirmar Promoción?',
        text: 'Este cristal va por promoción. ¿Desea activar esta opción?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, Activar Promoción',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });

      if (result.isConfirmed) {
        setDatosAnteojo(prev => ({
          ...prev,
          usa_promocion: true
        }));
      }
    } else {
      setDatosAnteojo(prev => ({
        ...prev,
        usa_promocion: false
      }));
    }
  };


  // Verificar duplicados al completar datos básicos del cliente
  const verificarDuplicados = async () => {
    // Limpiar y validar datos
    const nombre = datosCliente.nombre?.trim();
    const apellido = datosCliente.apellido?.trim(); 
    const telefono = datosCliente.telefono?.trim();

    if (!nombre || !apellido || !telefono) {
      alert("Por favor complete nombre, apellido y teléfono para continuar");
      return false;
    }

    try {
      const resultado = await verificarClienteDuplicadoAvanzado(nombre, apellido, telefono, true);
      if (resultado.duplicado) {
        // Guardar el objeto completo del cliente duplicado
        setClienteDuplicado(resultado.clientes_encontrados?.[0] || resultado.cliente_existente);
        setMostrarAdvertenciaDuplicado(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error al verificar duplicado:", error);
      alert(`Error al verificar duplicados: ${error.message}`);
      return false;
    }
  };

  // Continuar con cliente duplicado (crear anteojo adicional)
  const continuarConDuplicado = async () => {
    if (!clienteDuplicado) return;
    setClienteCreado(clienteDuplicado);
    setDatosCliente({
      nombre: clienteDuplicado.nombre,
      apellido: clienteDuplicado.apellido,
      telefono: clienteDuplicado.telefono,
      fecha: clienteDuplicado.fecha_registro || new Date().toISOString().split('T')[0],
    });
    setMostrarAdvertenciaDuplicado(false);

    // Buscar el último anteojo del cliente usando la API
    try {
      const clienteCompleto = await obtenerClienteCompleto(clienteDuplicado.id);
      if (clienteCompleto && Array.isArray(clienteCompleto.anteojos) && clienteCompleto.anteojos.length > 0) {
        // Ordenar por id descendente (más reciente primero)
        const anteojosOrdenados = [...clienteCompleto.anteojos].sort((a, b) => (b.id || 0) - (a.id || 0));
        setUltimoAnteojo(anteojosOrdenados[0]);
        // Forzar mostrar el modal después de setear el anteojo
        setTimeout(() => {
          setMostrarModalReplicar(true);
        }, 0);
        // No avanzar a etapa 2 hasta que el usuario decida
      } else {
        setUltimoAnteojo(null);
        setMostrarModalReplicar(false);
        setEtapaActual(2);
      }
    } catch (e) {
      setUltimoAnteojo(null);
      setMostrarModalReplicar(false);
      setEtapaActual(2);
    }
  };

  const replicarGraduacion = () => {
    if (!ultimoAnteojo) return;
    setDatosAnteojo(prev => ({
      ...prev,
      // copiar el tipo de anteojo del último registro
      tipo_anteojo: ultimoAnteojo.tipo_anteojo || prev.tipo_anteojo || 'Monofocal',
      // si el tipo requiere adición, copiarla; en otros casos dejar vacía
      od_adicion: (['Multifocal','Bifocal','Ocupacional'].includes(ultimoAnteojo.tipo_anteojo) && (ultimoAnteojo.od_adicion !== undefined && ultimoAnteojo.od_adicion !== null))
        ? String(ultimoAnteojo.od_adicion)
        : prev.od_adicion || "",
      oi_adicion: (['Multifocal','Bifocal','Ocupacional'].includes(ultimoAnteojo.tipo_anteojo) && (ultimoAnteojo.oi_adicion !== undefined && ultimoAnteojo.oi_adicion !== null))
        ? String(ultimoAnteojo.oi_adicion)
        : prev.oi_adicion || "",
      lejos_esf_od: ultimoAnteojo.lejos_esf_od ? String(ultimoAnteojo.lejos_esf_od) : "",
      lejos_cil_od: ultimoAnteojo.lejos_cil_od ? String(ultimoAnteojo.lejos_cil_od) : "",
      lejos_eje_od: ultimoAnteojo.lejos_eje_od ? String(ultimoAnteojo.lejos_eje_od) : "",
      lejos_esf_oi: ultimoAnteojo.lejos_esf_oi ? String(ultimoAnteojo.lejos_esf_oi) : "",
      lejos_cil_oi: ultimoAnteojo.lejos_cil_oi ? String(ultimoAnteojo.lejos_cil_oi) : "",
      lejos_eje_oi: ultimoAnteojo.lejos_eje_oi ? String(ultimoAnteojo.lejos_eje_oi) : "",
      cerca_esf_od: ultimoAnteojo.cerca_esf_od ? String(ultimoAnteojo.cerca_esf_od) : "",
      cerca_cil_od: ultimoAnteojo.cerca_cil_od ? String(ultimoAnteojo.cerca_cil_od) : "",
      cerca_eje_od: ultimoAnteojo.cerca_eje_od ? String(ultimoAnteojo.cerca_eje_od) : "",
      cerca_esf_oi: ultimoAnteojo.cerca_esf_oi ? String(ultimoAnteojo.cerca_esf_oi) : "",
      cerca_cil_oi: ultimoAnteojo.cerca_cil_oi ? String(ultimoAnteojo.cerca_cil_oi) : "",
      cerca_eje_oi: ultimoAnteojo.cerca_eje_oi ? String(ultimoAnteojo.cerca_eje_oi) : "",
  lejos_cristal: ultimoAnteojo.lejos_cristal || "",
  // dejar armazón en blanco porque normalmente cambia al replicar
  lejos_armazon: "",
  lejos_armazon_id: null,
      cerca_cristal: ultimoAnteojo.cerca_cristal || "",
  // dejar armazón en blanco porque normalmente cambia al replicar
  cerca_armazon: "",
  cerca_armazon_id: null,
      altura_od: ultimoAnteojo.altura_od ? String(ultimoAnteojo.altura_od) : "",
      altura_oi: ultimoAnteojo.altura_oi ? String(ultimoAnteojo.altura_oi) : "",
      dnp_od: ultimoAnteojo.dnp_od ? String(ultimoAnteojo.dnp_od) : "",
      dnp_oi: ultimoAnteojo.dnp_oi ? String(ultimoAnteojo.dnp_oi) : "",
      grados_prisma_od: ultimoAnteojo.grados_prisma_od ? String(ultimoAnteojo.grados_prisma_od) : "",
      grados_prisma_oi: ultimoAnteojo.grados_prisma_oi ? String(ultimoAnteojo.grados_prisma_oi) : "",
      base_od: ultimoAnteojo.base_od || "",
      base_oi: ultimoAnteojo.base_oi || "",
      observaciones: ultimoAnteojo.observaciones || ""
    }));
    setMostrarModalReplicar(false);
    setEtapaActual(2);
  };

  const cargarEnBlanco = async () => {
    setMostrarModalReplicar(false);
    setEtapaActual(2);
    setCristalLejos(null);
    setCristalCerca(null);
    setArmazonLejos(null);
    setArmazonCerca(null);
    setPreciosCristal({ lejos: 0, cerca: 0 });
    setPreciosArmazon({ lejos: 0, cerca: 0 });
    // Inicializar datos del anteojo en blanco, pero mantener nro_sobre actualizado
    const proximoNumero = await obtenerProximoNumeroSobre();
    const observacionAutomatica = construirObservacionBeneficio(beneficioForzado, ultimoAnteojo?.nro_sobre || ultimoAnteojoProp?.nro_sobre);
    setDatosAnteojo({
      nro_sobre: proximoNumero.toString(),
      tipo_anteojo: "Monofocal",
      lejos_esf_od: "",
      lejos_cil_od: "",
      lejos_eje_od: "",
      lejos_esf_oi: "",
      lejos_cil_oi: "",
      lejos_eje_oi: "",
      lejos_cristal: "",
      lejos_armazon: "",
      lejos_armazon_id: null,
      cerca_esf_od: "",
      cerca_cil_od: "",
      cerca_eje_od: "",
      cerca_esf_oi: "",
      cerca_cil_oi: "",
      cerca_eje_oi: "",
      cerca_cristal: "",
      cerca_armazon: "",
      cerca_armazon_id: null,
      altura_od: "",
      altura_oi: "",
      dnp_od: "",
      dnp_oi: "",
      grados_prisma_od: "",
      grados_prisma_oi: "",
      base_od: "",
      base_oi: "",
      observaciones: observacionAutomatica,
      precio_total: "",
      sena: "",
      tipo_pago: "efectivo",
      od_adicion: "",
      oi_adicion: "",
      usa_seguro: beneficioForzado === 'seguro',
      usa_promocion: beneficioForzado === 'promocion'
    });
  };

  // Avanzar a la etapa 2 (datos del anteojo)
  const avanzarAEtapa2 = async () => {
    const usuarioActual = authService.obtenerUsuarioActual();
    if (isDemoUser(usuarioActual)) {
      solicitarRegistroDemo('registrar tus propios pacientes y recetas');
      return;
    }

    const puedeAvanzar = await verificarDuplicados();
    if (puedeAvanzar) {
      // Crear el cliente
      try {
        const clienteData = {
          nombre: datosCliente.nombre,
          apellido: datosCliente.apellido,
          telefono: datosCliente.telefono,
          padre_madre_encargado: datosCliente.padre_madre_encargado || null,
          domicilio: datosCliente.domicilio || null,
          email: datosCliente.email || null,
          fecha_registro: datosCliente.fecha || new Date().toISOString().split('T')[0]
        };

        const clienteResponse = await crearCliente(clienteData);
        setClienteCreado({ ...clienteData, id: clienteResponse.cliente_id });
        setEtapaActual(2);
        
        // Obtener próximo número de sobre
        const proximoNumero = await obtenerProximoNumeroSobre();
        setDatosAnteojo(prev => ({ ...prev, nro_sobre: proximoNumero.toString() }));
        
      } catch (error) {
        console.error("Error al crear cliente:", error);
        if (error.message.includes("Ya existe un cliente con ese nombre y teléfono")) {
          alert("Ya existe un cliente con ese nombre y teléfono. Por favor verifique los datos o modifique el cliente existente.");
        } else {
          alert("Error al crear cliente: " + error.message);
        }
      }
    }
  };

  // Retroceder a la etapa 1
  const retrocederAEtapa1 = () => {
    setEtapaActual(1);
  };

  // Calcular saldo automáticamente para la etapa 2
  const saldoCalculado = calcularSaldo(datosAnteojo.precio_total, datosAnteojo.sena);

  // Finalizar proceso creando el anteojo (etapa 2)
  const finalizarProceso = async (e) => {
    e.preventDefault();

    const usuarioActual = authService.obtenerUsuarioActual();
    if (isDemoUser(usuarioActual)) {
      solicitarRegistroDemo('guardar y procesar anteojos en el taller');
      return;
    }

    if (!clienteCreado) {
      alert("Error: No hay cliente creado");
      return;
    }

    try {
      const observacionAutomatica = construirObservacionBeneficio(beneficioForzado, ultimoAnteojo?.nro_sobre || ultimoAnteojoProp?.nro_sobre);
      const usaSeguroFinal = beneficioForzado === 'seguro' ? true : (datosAnteojo.usa_seguro || false);
      const usaPromocionFinal = beneficioForzado === 'promocion' ? true : (datosAnteojo.usa_promocion || false);

      const anteojoData = {
        nro_sobre: parseInt(datosAnteojo.nro_sobre) || null,
        fecha_pedido: datosCliente.fecha || new Date().toISOString().split('T')[0],
        tipo_anteojo: datosAnteojo.tipo_anteojo || "Monofocal",
        lejos_esf_od: parseFloat(datosAnteojo.lejos_esf_od) || null,
        lejos_cil_od: parseFloat(datosAnteojo.lejos_cil_od) || null,
        lejos_eje_od: parseInt(datosAnteojo.lejos_eje_od) || null,
        lejos_esf_oi: parseFloat(datosAnteojo.lejos_esf_oi) || null,
        lejos_cil_oi: parseFloat(datosAnteojo.lejos_cil_oi) || null,
        lejos_eje_oi: parseInt(datosAnteojo.lejos_eje_oi) || null,
        lejos_cristal: datosAnteojo.lejos_cristal || null,
        lejos_armazon: datosAnteojo.lejos_armazon || null,
        lejos_armazon_id: datosAnteojo.lejos_armazon_id || null,
        cerca_esf_od: parseFloat(datosAnteojo.cerca_esf_od) || null,
        cerca_cil_od: parseFloat(datosAnteojo.cerca_cil_od) || null,
        cerca_eje_od: parseInt(datosAnteojo.cerca_eje_od) || null,
        cerca_esf_oi: parseFloat(datosAnteojo.cerca_esf_oi) || null,
        cerca_cil_oi: parseFloat(datosAnteojo.cerca_cil_oi) || null,
        cerca_eje_oi: parseInt(datosAnteojo.cerca_eje_oi) || null,
        cerca_cristal: datosAnteojo.cerca_cristal || null,
        cerca_armazon: datosAnteojo.cerca_armazon || null,
        cerca_armazon_id: datosAnteojo.cerca_armazon_id || null,
        altura_od: parseFloat(datosAnteojo.altura_od) || null,
        altura_oi: parseFloat(datosAnteojo.altura_oi) || null,
        dnp_od: parseFloat(datosAnteojo.dnp_od) || null,
        dnp_oi: parseFloat(datosAnteojo.dnp_oi) || null,
        grados_prisma_od: parseFloat(datosAnteojo.grados_prisma_od) || null,
        grados_prisma_oi: parseFloat(datosAnteojo.grados_prisma_oi) || null,
        base_od: datosAnteojo.base_od || null,
        base_oi: datosAnteojo.base_oi || null,
        observaciones: combinarObservaciones(datosAnteojo.observaciones, observacionAutomatica),
        precio_total: parseFloat(datosAnteojo.precio_total) || 0,
        sena: parseFloat(datosAnteojo.sena) || 0,
        tipo_pago: datosAnteojo.tipo_pago || "efectivo",
        pago_efectivo: datosAnteojo.tipo_pago === 'efectivo' || datosAnteojo.tipo_pago === 'transferencia',
        usa_seguro: usaSeguroFinal,
        usa_promocion: usaPromocionFinal,
        estado: "Tomado",
        cliente_id: clienteCreado.id,
        od_adicion: parseFloat(datosAnteojo.od_adicion) || 0,
        oi_adicion: parseFloat(datosAnteojo.oi_adicion) || 0
      };

      console.log("Datos del anteojo a crear:", anteojoData);
      await agregarAnteojo(clienteCreado.id, anteojoData);


      const navegarHacia = (vista) => {
        if (onClose) {
          onClose();
        }
        if (onViewChange) {
          onViewChange(vista);
        } else {
          try {
            localStorage.setItem('currentView', vista);
            globalThis.dispatchEvent(new CustomEvent('app-view-change', { detail: { view: vista } }));
          } catch (_e) {
            // fallback
          }
        }
      };

      const reiniciarFormulario = async () => {
        setEtapaActual(1);
        setClienteCreado(null);
        setCristalLejos(null);
        setCristalCerca(null);
        setArmazonLejos(null);
        setArmazonCerca(null);
        setPreciosCristal({ lejos: 0, cerca: 0 });
        setPreciosArmazon({ lejos: 0, cerca: 0 });
        setDatosCliente({
          nombre: "",
          apellido: "",
          telefono: "",
          padre_madre_encargado: "",
          domicilio: "",
          email: "",
          obra_social: "",
          observaciones: "",
          fecha: new Date().toISOString().split('T')[0],
        });
        const proximoNumero = await obtenerProximoNumeroSobre();
        setDatosAnteojo({
          nro_sobre: proximoNumero.toString(),
          lejos_esf_od: "",
          lejos_cil_od: "",
          lejos_eje_od: "",
          lejos_esf_oi: "",
          lejos_cil_oi: "",
          lejos_eje_oi: "",
          lejos_cristal: "",
          lejos_armazon: "",
          lejos_armazon_id: null,
          cerca_esf_od: "",
          cerca_cil_od: "",
          cerca_eje_od: "",
          cerca_esf_oi: "",
          cerca_cil_oi: "",
          cerca_eje_oi: "",
          cerca_cristal: "",
          cerca_armazon: "",
          cerca_armazon_id: null,
          altura_od: "",
          altura_oi: "",
          dnp_od: "",
          dnp_oi: "",
          grados_prisma_od: "",
          grados_prisma_oi: "",
          base_od: "",
          base_oi: "",
          observaciones: "",
          precio_total: "",
          sena: "",
          tipo_pago: "efectivo",
          tipo_anteojo: "Monofocal",
          od_adicion: "",
          oi_adicion: "",
          usa_seguro: false,
          usa_promocion: false
        });
      };

      const saldoNum = parseFloat(datosAnteojo.saldo) || 0;
      const esTotalAbonado = saldoNum <= 0;

      if (esTotalAbonado) {
        const result = await Swal.fire({
          icon: 'success',
          title: '¡Cliente y anteojo creados!',
          text: 'El monto total fue abonado. ¿Desea realizar la factura?',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: 'Realizar Factura',
          denyButtonText: 'Ir a Lista de Clientes',
          cancelButtonText: 'Crear Cliente Nuevo',
          confirmButtonColor: '#059669',
          denyButtonColor: '#2563eb',
          cancelButtonColor: '#64748b'
        });

        if (result.isConfirmed) {
          await solicitarFacturacionAnteojo({
            cliente: clienteCreado,
            anteojo: { ...datosAnteojo, ...anteojoData },
            onViewChange,
            onCloseModal: onClose
          });
          return;
        } else if (result.isDenied) {
          navegarHacia('clientes');
          return;
        } else {
          if (onClose) {
            navegarHacia('nuevo-cliente');
          } else {
            await reiniciarFormulario();
          }
          return;
        }
      }

      const result = await Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Cliente y anteojo creados exitosamente!',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Ir a Lista de Clientes',
        denyButtonText: 'Crear Cliente Nuevo',
        cancelButtonText: 'Ir al Dashboard',
        cancelButtonColor: '#6366f1'
      });

      if (result.isConfirmed) {
        navegarHacia('clientes');
        return;
      } else if (result.isDenied) {
        if (onClose) {
          navegarHacia('nuevo-cliente');
        } else {
          await reiniciarFormulario();
        }
        return;
      } else if (result.isDismissed) {
        navegarHacia('dashboard');
        return;
      }

    } catch (error) {
      console.error("Error al crear anteojo:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error al crear anteojo',
        html: `<textarea style='width:100%;height:120px' readonly>${error?.message || error?.toString() || ''}</textarea>`,
        confirmButtonText: 'Cerrar',
      });
    }
  };

  // Cancelar y editar datos del cliente (cierra el modal de duplicado)
  const cancelarDuplicado = () => {
    setMostrarAdvertenciaDuplicado(false);
    setClienteDuplicado(null);
  };

  // Inicializar número de sobre al montar el componente
  useEffect(() => {
    const inicializarNumeroSobre = async () => {
      const proximoNumero = await obtenerProximoNumeroSobre();
      setDatosAnteojo(prev => ({
        ...prev,
        nro_sobre: proximoNumero.toString()
      }));
    };

    inicializarNumeroSobre();
  }, []);

  // Función para validar graduación
  const validarGraduacion = (valor) => {
    if (!valor && valor !== 0) return { valido: true, tipo: 'vacio' };
    
    // Convertir a string si es número
    const valorStr = String(valor);
    
    // Patrón para graduación: +/- seguido de número con .00, .25, .50, .75
    const patron = /^[+\-]?\d{1,2}\.(00|25|50|75)$/;
    
    if (patron.test(valorStr)) {
      return { valido: true, tipo: 'correcto' };
    }
    
    // Si es un número válido pero sin formato completo
    const numero = parseFloat(valorStr.replace(/[+\-]/g, ''));
    if (!isNaN(numero) && valorStr.match(/^[+\-]?\d+(\.\d*)?$/)) {
      return { valido: false, tipo: 'incompleto' };
    }
    
    return { valido: false, tipo: 'incorrecto' };
  };

  // Función para formatear graduación al salir del campo
  const formatearGraduacionCompleta = (valor) => {
    if (!valor && valor !== 0) return '';
    
    // Convertir a string si es número
    const valorStr = String(valor);
    
    // Limpiar caracteres no válidos
    let limpio = valorStr.replace(/[^+\-0-9.]/g, '');
    
    // Extraer signo y número
    const esNegativo = limpio.startsWith('-');
    const numero = parseFloat(limpio.replace(/[+\-]/g, ''));
    
    if (isNaN(numero)) return valor;
    
    // Redondear a múltiplos de 0.25
    const redondeado = Math.round(numero * 4) / 4;
    
    // Formatear con signo
    const signo = esNegativo ? '-' : '+';
    return `${signo}${redondeado.toFixed(2)}`;
  };

  // Manejar cambios en campos de graduación (sin formateo automático)
  const handleGraduacionChange = (e) => {
    const { name, value } = e.target;
    setDatosAnteojo({ ...datosAnteojo, [name]: value });
  };

  // Formatear al salir del campo
  const handleGraduacionBlur = (e) => {
    const { name, value } = e.target;
    const camposGraduacion = ['lejos_esf_od', 'lejos_cil_od', 'lejos_esf_oi', 'lejos_cil_oi', 
                              'cerca_esf_od', 'cerca_cil_od', 'cerca_esf_oi', 'cerca_cil_oi'];
    
    if (camposGraduacion.includes(name) && value) {
      const valorFormateado = formatearGraduacionCompleta(value);
      setDatosAnteojo({ ...datosAnteojo, [name]: valorFormateado });
    }
  };

  // Manejar datos extraídos del escaneo de ficha
  const manejarDatosEscaneados = (datosExtraidos) => {
    // Actualizar datos del cliente (etapa 1)
    if (datosExtraidos.nombre || datosExtraidos.apellido || datosExtraidos.telefono || datosExtraidos.fecha) {
      setDatosCliente(prev => ({
        ...prev,
        nombre: datosExtraidos.nombre || prev.nombre,
        apellido: datosExtraidos.apellido || prev.apellido,
        telefono: datosExtraidos.telefono || prev.telefono,
        fecha: datosExtraidos.fecha || prev.fecha
      }));
    }

    // Actualizar datos del anteojo (etapa 2)
    const camposAnteojo = [
      'lejos_esf_od', 'lejos_cil_od', 'lejos_eje_od',
      'lejos_esf_oi', 'lejos_cil_oi', 'lejos_eje_oi',
      'cerca_esf_od', 'cerca_cil_od', 'cerca_eje_od',
      'cerca_esf_oi', 'cerca_cil_oi', 'cerca_eje_oi',
      'lejos_cristal', 'lejos_armazon', 'cerca_cristal', 'cerca_armazon',
      'altura_od', 'altura_oi', 'dnp_od', 'dnp_oi',
      'grados_prisma_od', 'grados_prisma_oi', 'base_od', 'base_oi',
      'precio_total', 'sena', 'observaciones'
    ];

    const datosAnteojoActualizados = { ...datosAnteojo };
    camposAnteojo.forEach(campo => {
      if (datosExtraidos[campo]) {
        datosAnteojoActualizados[campo] = datosExtraidos[campo];
      }
    });

    setDatosAnteojo(datosAnteojoActualizados);

    // Mostrar mensaje de éxito
    Swal.fire({
      title: '¡Datos Importados!',
      text: 'Los datos de la ficha han sido importados. Revise y complete la información faltante.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  };

  // Función para limpiar campo
  const limpiarCampo = (nombreCampo) => {
    if (nombreCampo.includes('cliente')) {
      setDatosCliente({ ...datosCliente, [nombreCampo.replace('cliente_', '')]: '' });
    } else {
      setDatosAnteojo({ ...datosAnteojo, [nombreCampo]: '' });
    }
  };

  // Función para manejar selección de cristal y obtener sus capacidades
  const handleCristalChange = async (e) => {
    const { name, value } = e.target;
    
    // Actualizar el valor en el formulario
    handleAnteojoChange(e);
    
    // Si se seleccionó un cristal, obtener su información completa
    if (value) {
      try {
        console.log('🔍 Buscando cristal:', value, 'para tipo:', datosAnteojo.tipo_anteojo);
        const response = await obtenerCristalesPorTipo(datosAnteojo.tipo_anteojo);
        const cristales = response.cristales || [];
        console.log('📦 Cristales encontrados:', cristales);
        
        // Buscar el cristal seleccionado por marca (o por el display completo, incluyendo producto/material)
        const cristalSeleccionado = cristales.find(c => {
          if (c.marca === value) return true;
          // Formato antiguo: "tipo_vision - marca"
          if (`${c.tipo_vision} - ${c.marca}` === value) return true;
          // Formato nuevo con producto y/o material
          const partes = [c.tipo_vision, '-', c.marca];
          if (c.producto) partes.push('-', c.producto);
          if (c.material) partes.push(`(${c.material})`);
          return partes.join(' ') === value;
        });
        
        console.log('✅ Cristal seleccionado:', cristalSeleccionado);
        
        if (cristalSeleccionado) {
          if (name === 'lejos_cristal') {
            setCristalLejos(cristalSeleccionado);
          } else if (name === 'cerca_cristal') {
            setCristalCerca(cristalSeleccionado);
          }
        }
      } catch (error) {
        console.error('❌ Error al obtener información del cristal:', error);
      }
    } else {
      // Si se limpia el cristal, limpiar también los estados
      if (name === 'lejos_cristal') {
        setCristalLejos(null);
        // Limpiar checkboxes si se basa solo en cristal de lejos
        if (!beneficioForzado) {
          setDatosAnteojo(prev => ({ ...prev, usa_seguro: false, usa_promocion: false }));
        }
      } else if (name === 'cerca_cristal') {
        setCristalCerca(null);
      }
    }
  };

  // Función para manejar selección de armazón y obtener su información
  const handleArmazonChange = async (e) => {
    const { name, value } = e.target;
    
    // Actualizar el valor en el formulario
    handleAnteojoChange(e);
    
    // Si se seleccionó un armazón, obtener su información completa
    if (value) {
      try {
        const response = await obtenerArmazonesDisponibles();
        const armazones = response.armazones || [];
        
        console.log('🔍 Buscando armazón:', value);
        console.log('📦 Armazones disponibles:', armazones);
        
        // Buscar el armazón seleccionado
        const armazonSeleccionado = armazones.find(a => 
          `${a.marca} ${a.modelo} - ${a.color}` === value ||
          `${a.marca} ${a.modelo}` === value
        );
        
        console.log('✅ Armazón encontrado:', armazonSeleccionado);
        
        if (armazonSeleccionado) {
          // Guardar el ID del armazón para descontar stock
          setDatosAnteojo(prev => ({
            ...prev,
            [name + '_id']: armazonSeleccionado.id,
          }));

          if (name === 'lejos_armazon') {
            setArmazonLejos(armazonSeleccionado);
          } else if (name === 'cerca_armazon') {
            setArmazonCerca(armazonSeleccionado);
          }
        }
      } catch (error) {
        console.error('❌ Error al obtener información del armazón:', error);
      }
    } else {
      // Si se limpia el armazón, limpiar también el objeto y el ID
      if (name === 'lejos_armazon') {
        setArmazonLejos(null);
        setDatosAnteojo(prev => ({ ...prev, lejos_armazon_id: null }));
      } else if (name === 'cerca_armazon') {
        setArmazonCerca(null);
        setDatosAnteojo(prev => ({ ...prev, cerca_armazon_id: null }));
      }
    }
  };

  return (
    <div className="cliente-form-bg">
      <div className="cliente-form-container">
       
        {/* Contenido con scroll */}
        <div className="cliente-form-content">
          {/* Indicador de progreso */}
          <div className="progress-indicator">
            <div className={`step ${
              modoAgregarAnteojo ? 'completed' : 
              (etapaActual === 1 ? 'active' : etapaActual > 1 ? 'completed' : '')
            }`}>
              <span className="step-number">1</span>
              <span className="step-label">Datos del Cliente</span>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${
              modoAgregarAnteojo ? 'active' : 
              (etapaActual === 2 ? 'active' : '')
            }`}>
              <span className="step-number">2</span>
              <span className="step-label">Datos del Anteojo</span>
            </div>
          </div>

      {/* Modal de advertencia de duplicado */}
      {mostrarAdvertenciaDuplicado && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Cliente Duplicado Encontrado</h2>
            </div>
            <div className="modal-body">
              <p><strong>Ya existe un cliente con los siguientes datos:</strong></p>
              <div className="duplicate-info">
                <p><strong>Nombre:</strong> {clienteDuplicado?.nombre} {clienteDuplicado?.apellido}</p>
                <p><strong>Teléfono:</strong> {clienteDuplicado?.telefono}</p>
                <p><strong>Fecha de registro:</strong> {clienteDuplicado?.fecha_registro}</p>
                {clienteDuplicado?.created_at && (
                  <p><strong>Creado:</strong> {clienteDuplicado.created_at}</p>
                )}
                {clienteDuplicado?.updated_at && (
                  <p><strong>Actualizado:</strong> {clienteDuplicado.updated_at}</p>
                )}
                {clienteDuplicado?.similitud !== undefined && (
                  <p><strong>Similitud:</strong> {Math.round(clienteDuplicado.similitud * 100)}%</p>
                )}
              </div>
              <p>¿Desea continuar agregando un nuevo anteojo a este cliente existente o modificar los datos?</p>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={cancelarDuplicado} className="btn-secondary">
                Modificar Datos
              </button>
              <button type="button" onClick={continuarConDuplicado} className="btn-primary">
                Continuar con Cliente Existente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para replicar graduación */}
      {mostrarModalReplicar && ultimoAnteojo && (
        <div className="modal-overlay" onClick={() => setMostrarModalReplicar(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>¿Replicar graduación del último anteojo?</h2>
            </div>
            <div className="modal-body">
              <p>Se encontró un anteojo anterior para este cliente. ¿Desea copiar la graduación?</p>
              <div className="duplicate-info">
                <p><strong>Lejos OD:</strong> {ultimoAnteojo.lejos_esf_od} / {ultimoAnteojo.lejos_cil_od} / {ultimoAnteojo.lejos_eje_od}</p>
                <p><strong>Lejos OI:</strong> {ultimoAnteojo.lejos_esf_oi} / {ultimoAnteojo.lejos_cil_oi} / {ultimoAnteojo.lejos_eje_oi}</p>
                <p><strong>Cerca OD:</strong> {ultimoAnteojo.cerca_esf_od} / {ultimoAnteojo.cerca_cil_od} / {ultimoAnteojo.cerca_eje_od}</p>
                <p><strong>Cerca OI:</strong> {ultimoAnteojo.cerca_esf_oi} / {ultimoAnteojo.cerca_cil_oi} / {ultimoAnteojo.cerca_eje_oi}</p>
                <p><strong>Observaciones:</strong> {ultimoAnteojo.observaciones}</p>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={cargarEnBlanco} className="btn-secondary">
                Cargar en blanco
              </button>
              <button type="button" onClick={replicarGraduacion} className="btn-primary">
                Replicar graduación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ETAPA 1: Datos del Cliente */}
      {!modoAgregarAnteojo && etapaActual === 1 && (
        <form className="form" onSubmit={(e) => { e.preventDefault(); avanzarAEtapa2(); }}>
          <h2>Etapa 1: Datos del Cliente</h2>
          <div className="form-row">
            <label>
              Fecha:
              <input
                type="date"
                name="fecha"
                value={datosCliente.fecha}
                onChange={handleClienteChange}
              />
            </label>
           </div>
          <div className="form-row">
            <label>
              Nombre:
              <input
                type="text"
                name="nombre"
                value={datosCliente.nombre}
                onChange={handleClienteChange}
                required
              />
            </label>
            <label>
              Apellido:
              <input
                type="text"
                name="apellido"
                value={datosCliente.apellido}
                onChange={handleClienteChange}
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Teléfono:
              <input
                type="text"
                name="telefono"
                value={datosCliente.telefono}
                onChange={handleClienteChange}
                required
              />
            </label>
            <label>
              Padre/Madre/Encargado:
              <input
                type="text"
                name="padre_madre_encargado"
                value={datosCliente.padre_madre_encargado}
                onChange={handleClienteChange}
                placeholder="Opcional"
              />
            </label>
          </div>

          <CollapsibleSection
            title="Información Adicional"
            isExpanded={clienteInfoExpanded}
            onToggle={() => setClienteInfoExpanded(!clienteInfoExpanded)}
          >
            <div className="form-row">
            <label>
              Domicilio:
              <input
                type="text"
                name="domicilio"
                value={datosCliente.domicilio}
                onChange={handleClienteChange}
                placeholder="Opcional"
              />
            </label>
            <label>
              E-mail:
              <input
                type="email"
                name="email"
                value={datosCliente.email}
                onChange={handleClienteChange}
                placeholder="Opcional"
              />
            </label>
          </div>
            <div className="form-row">
              <label>
                Obra Social:
                <input
                  type="text"
                  name="obra_social"
                  value={datosCliente.obra_social}
                  onChange={handleClienteChange}
                  placeholder="Opcional"
                />
              </label>
            </div>

            <div className="form-row">
              <label>
                Observaciones:
                <textarea
                  name="observaciones"
                  value={datosCliente.observaciones}
                  onChange={handleClienteChange}
                  placeholder="Opcional"
                  style={{ minHeight: '80px', fontFamily: 'inherit' }}
                />
              </label>
            </div>
          </CollapsibleSection>       
          <div className="form-actions">
            <button type="submit" className="btn-primary" style={{ marginLeft: 'auto' }}>
              Continuar al Paso 2 →
            </button>
          </div>
          
        </form>
      )}

      {/* ETAPA 2: Datos del Anteojo */}
      {etapaActual === 2 && (
        <form className="form" onSubmit={finalizarProceso}>
          <h2>Etapa 2: Datos del Anteojo</h2>
          
          {clienteCreado && (
            <div className="client-info">
              <h3>Cliente: {clienteCreado.nombre} {clienteCreado.apellido}</h3>
              <p>Teléfono: {clienteCreado.telefono}</p>
              {clienteCreado.padre_madre_encargado && (
                <p>Padre/Madre/Encargado: {clienteCreado.padre_madre_encargado}</p>
              )}
            </div>
          )}

          <div className="form-row" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <label style={{ flex: 1 }}>
              Número de Sobre:
              <input
                type="number"
                name="nro_sobre"
                value={datosAnteojo.nro_sobre}
                onChange={handleAnteojoChange}
                required
              />
            </label>
            <label style={{ flex: 1 }}>
              Tipo de Anteojo:
              <select
                name="tipo_anteojo"
                value={datosAnteojo.tipo_anteojo}
                onChange={handleAnteojoChange}
                style={{ width: '100%' }}
              >
                <option value="Monofocal">Monofocal</option>
                <option value="Ocupacional">Ocupacional</option>
                <option value="Bifocal">Bifocal</option>
                <option value="Multifocal">Multifocal</option>
                <option value="Lentes de Contacto">Lentes de Contacto</option>
                <option value="Sol">Sol</option>
                <option value="Otros">Otros</option>
              </select>
            </label>
          </div>

          <CollapsibleSection
            title="Lejos"
            isExpanded={lejosExpanded}
            onToggle={() => setLejosExpanded(!lejosExpanded)}
          >
            <div className="eye-sections">
              <div className="eye-section">
                <h4>Ojo Derecho (OD)</h4>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="lejos_esf_od"
                    placeholder="Esfera (+/-X.XX)"
                    value={datosAnteojo.lejos_esf_od}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.lejos_esf_od ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.lejos_esf_od);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.lejos_esf_od && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_esf_od);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_esf_od);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('lejos_esf_od')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="lejos_cil_od"
                    placeholder="Cilindro (+/-X.XX)"
                    value={datosAnteojo.lejos_cil_od}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.lejos_cil_od ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.lejos_cil_od);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.lejos_cil_od && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_cil_od);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_cil_od);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('lejos_cil_od')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="number"
                    name="lejos_eje_od"
                    placeholder="Eje"
                    value={datosAnteojo.lejos_eje_od}
                    onChange={handleAnteojoChange}
                    style={{ paddingRight: datosAnteojo.lejos_eje_od ? '25px' : '10px' }}
                  />
                  {datosAnteojo.lejos_eje_od && (
                    <button
                      type="button"
                      onClick={() => limpiarCampo('lejos_eje_od')}
                      style={{
                        position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                        border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                      }}
                    >✕</button>
                  )}
                </div>
              </div>
              <div className="eye-section">
                <h4>Ojo Izquierdo (OI)</h4>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="lejos_esf_oi"
                    placeholder="Esfera (+/-X.XX)"
                    value={datosAnteojo.lejos_esf_oi}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.lejos_esf_oi ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.lejos_esf_oi);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.lejos_esf_oi && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_esf_oi);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_esf_oi);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('lejos_esf_oi')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="lejos_cil_oi"
                    placeholder="Cilindro (+/-X.XX)"
                    value={datosAnteojo.lejos_cil_oi}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.lejos_cil_oi ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.lejos_cil_oi);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.lejos_cil_oi && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_cil_oi);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.lejos_cil_oi);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('lejos_cil_oi')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="number"
                    name="lejos_eje_oi"
                    placeholder="Eje"
                    value={datosAnteojo.lejos_eje_oi}
                    onChange={handleAnteojoChange}
                    style={{ paddingRight: datosAnteojo.lejos_eje_oi ? '25px' : '10px' }}
                  />
                  {datosAnteojo.lejos_eje_oi && (
                    <button
                      type="button"
                      onClick={() => limpiarCampo('lejos_eje_oi')}
                      style={{
                        position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                        border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                      }}
                    >✕</button>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              {datosAnteojo.tipo_anteojo === 'Lentes de Contacto' ? (
                <label>
                  Tipo de lentes de contacto:
                  <input
                    type="text"
                    name="lejos_cristal"
                    value={datosAnteojo.lejos_cristal}
                    onChange={handleAnteojoChange}
                  />
                </label>
              ) : (
                <>
                  <label>
                    Cristal:
                    <CristalSearchInput
                      name="lejos_cristal"
                      value={datosAnteojo.lejos_cristal}
                      onChange={handleCristalChange}
                      onCristalSelect={(cristal) => setCristalLejos(cristal)}
                      tipoAnteojo={datosAnteojo.tipo_anteojo}
                      placeholder="Buscar cristal para lejos..."
                    />
                  </label>
                  <label>
                    Armazón:
                    <ArmazonSearchInput
                      name="lejos_armazon"
                      value={datosAnteojo.lejos_armazon}
                      onChange={handleArmazonChange}
                      onArmazonSelect={(armazon) => {
                        setArmazonLejos(armazon);
                        setDatosAnteojo(prev => ({ ...prev, lejos_armazon_id: armazon.id }));
                      }}
                      placeholder="Buscar armazón para lejos..."
                    />
                  </label>
                </>
              )}
            </div>
            {beneficioForzado && (
              <div className="form-row" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#eefaf2', borderRadius: '4px', border: '1px solid #8fd1a6' }}>
                <h4 style={{ margin: 0, fontSize: '14px', color: '#1f6b3b' }}>
                  Beneficio aplicado automáticamente: {beneficioForzado === 'seguro' ? 'Seguro' : 'Promoción'}
                </h4>
              </div>
            )}

            {/* Checkboxes de Seguro y Promoción - aparecen solo si el cristal seleccionado los admite
            {!beneficioForzado && cristalLejos && (cristalLejos.tiene_seguro || cristalLejos.tiene_promocion) && (
              <div className="form-row" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Opciones disponibles para este cristal:</h4>
                {cristalLejos.tiene_seguro && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="usa_seguro"
                      checked={!!datosAnteojo.usa_seguro}
                      onChange={handleUsaSeguroChange}
                    />
                    Usa Seguro
                  </label>
                )}
                {cristalLejos.tiene_promocion && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="usa_promocion"
                      checked={!!datosAnteojo.usa_promocion}
                      onChange={handleUsaPromocionChange}
                    />
                    Usa Promoción
                  </label>
                )}
              </div>
            )}*/}

                {/* Campos Adición OD/OI - aparece debajo de Lejos (solo para ciertos tipos) */}
                {['Multifocal', 'Bifocal', 'Ocupacional'].includes(datosAnteojo.tipo_anteojo) && (
                  <div className="form-row" style={{ marginTop: '0.5rem' }}>
                    <label style={{ flex: 1 }}>
                      Adición OD:
                      <input
                        type="number"
                        name="od_adicion"
                        step="0.25"
                        value={datosAnteojo.od_adicion}
                        onChange={handleAnteojoChange}
                        placeholder="Ej: 1.00"
                      />
                    </label>
                    <label style={{ flex: 1 }}>
                      Adición OI:
                      <input
                        type="number"
                        name="oi_adicion"
                        step="0.25"
                        value={datosAnteojo.oi_adicion}
                        onChange={handleAnteojoChange}
                        placeholder="Ej: 1.00"
                      />
                    </label>
                  </div>
                )}
          </CollapsibleSection>

          {/* Sección de Cerca */}
          <CollapsibleSection
            title="Cerca"
            isExpanded={cercaExpanded}
            onToggle={() => setCercaExpanded(!cercaExpanded)}
          >
            <div className="eye-sections">
              <div className="eye-section">
                <h4>Ojo Derecho (OD)</h4>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="cerca_esf_od"
                    placeholder="Esfera (+/-X.XX)"
                    value={datosAnteojo.cerca_esf_od}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.cerca_esf_od ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.cerca_esf_od);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.cerca_esf_od && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_esf_od);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_esf_od);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('cerca_esf_od')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="cerca_cil_od"
                    placeholder="Cilindro (+/-X.XX)"
                    value={datosAnteojo.cerca_cil_od}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.cerca_cil_od ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.cerca_cil_od);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.cerca_cil_od && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_cil_od);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_cil_od);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('cerca_cil_od')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="number"
                    name="cerca_eje_od"
                    placeholder="Eje"
                    value={datosAnteojo.cerca_eje_od}
                    onChange={handleAnteojoChange}
                    style={{ paddingRight: datosAnteojo.cerca_eje_od ? '25px' : '10px' }}
                  />
                  {datosAnteojo.cerca_eje_od && (
                    <button
                      type="button"
                      onClick={() => limpiarCampo('cerca_eje_od')}
                      style={{
                        position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                        border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                      }}
                    >✕</button>
                  )}
                </div>
              </div>
              <div className="eye-section">
                <h4>Ojo Izquierdo (OI)</h4>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="cerca_esf_oi"
                    placeholder="Esfera (+/-X.XX)"
                    value={datosAnteojo.cerca_esf_oi}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.cerca_esf_oi ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.cerca_esf_oi);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.cerca_esf_oi && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_esf_oi);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_esf_oi);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('cerca_esf_oi')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    name="cerca_cil_oi"
                    placeholder="Cilindro (+/-X.XX)"
                    value={datosAnteojo.cerca_cil_oi}
                    onChange={handleGraduacionChange}
                    onBlur={handleGraduacionBlur}
                    style={{
                      paddingRight: datosAnteojo.cerca_cil_oi ? '50px' : '20px',
                      borderColor: (() => {
                        const validacion = validarGraduacion(datosAnteojo.cerca_cil_oi);
                        if (validacion.tipo === 'correcto') return '#10b981';
                        if (validacion.tipo === 'incompleto') return '#f59e0b';
                        if (validacion.tipo === 'incorrecto') return '#ef4444';
                        return '#d1d5db';
                      })()
                    }}
                  />
                  {datosAnteojo.cerca_cil_oi && (
                    <>
                      <span style={{
                        position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)',
                        color: (() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_cil_oi);
                          if (validacion.tipo === 'correcto') return '#10b981';
                          if (validacion.tipo === 'incompleto') return '#f59e0b';
                          return '#ef4444';
                        })()
                      }}>
                        {(() => {
                          const validacion = validarGraduacion(datosAnteojo.cerca_cil_oi);
                          if (validacion.tipo === 'correcto') return '✓';
                          if (validacion.tipo === 'incompleto') return '⚠';
                          return '✗';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={() => limpiarCampo('cerca_cil_oi')}
                        style={{
                          position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                        }}
                      >✕</button>
                    </>
                  )}
                </div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="number"
                    name="cerca_eje_oi"
                    placeholder="Eje"
                    value={datosAnteojo.cerca_eje_oi}
                    onChange={handleAnteojoChange}
                    style={{ paddingRight: datosAnteojo.cerca_eje_oi ? '25px' : '10px' }}
                  />
                  {datosAnteojo.cerca_eje_oi && (
                    <button
                      type="button"
                      onClick={() => limpiarCampo('cerca_eje_oi')}
                      style={{
                        position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)',
                        border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af'
                      }}
                    >✕</button>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              {datosAnteojo.tipo_anteojo === 'Lentes de Contacto' ? (
                <label>
                  Tipo de lentes de contacto:
                  <input
                    type="text"
                    name="cerca_cristal"
                    value={datosAnteojo.cerca_cristal}
                    onChange={handleAnteojoChange}
                  />
                </label>
              ) : (
                <>
                  <label>
                    Cristal:
                    <CristalSearchInput
                      name="cerca_cristal"
                      value={datosAnteojo.cerca_cristal}
                      onChange={handleCristalChange}
                      onCristalSelect={(cristal) => setCristalCerca(cristal)}
                      tipoAnteojo={datosAnteojo.tipo_anteojo}
                      placeholder="Buscar cristal para cerca..."
                    />
                  </label>
                  <label>
                    Armazón:
                    <ArmazonSearchInput
                      name="cerca_armazon"
                      value={datosAnteojo.cerca_armazon}
                      onChange={handleArmazonChange}
                      onArmazonSelect={(armazon) => {
                        setArmazonCerca(armazon);
                        setDatosAnteojo(prev => ({ ...prev, cerca_armazon_id: armazon.id }));
                      }}
                      placeholder="Buscar armazón para cerca..."
                    />
                  </label>
                </>
              )}
            </div>
          </CollapsibleSection>

          {/* Otras medidas */}
          <CollapsibleSection
            title="Otras Medidas"
            isExpanded={otrasMedidasExpanded}
            onToggle={() => setOtrasMedidasExpanded(!otrasMedidasExpanded)}
          >
            
            <div className="form-row">
              <label>
                DNP OD:
                <input
                  type="number"
                  name="dnp_od"
                  value={datosAnteojo.dnp_od}
                  onChange={handleAnteojoChange}
                  step="0.1"
                />
              </label>
              <label>
                DNP OI:
                <input
                  type="number"
                  name="dnp_oi"
                  value={datosAnteojo.dnp_oi}
                  onChange={handleAnteojoChange}
                  step="0.1"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Altura OD:
                <input
                  type="number"
                  name="altura_od"
                  value={datosAnteojo.altura_od}
                  onChange={handleAnteojoChange}
                  step="0.1"
                />
              </label>
              <label>
                Altura OI:
                <input
                  type="number"
                  name="altura_oi"
                  value={datosAnteojo.altura_oi}
                  onChange={handleAnteojoChange}
                  step="0.1"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Grados Prisma OD:
                <input
                  type="number"
                  name="grados_prisma_od"
                  value={datosAnteojo.grados_prisma_od}
                  onChange={handleAnteojoChange}
                  step="0.25"
                />
              </label>
              <label>
                Grados Prisma OI:
                <input
                  type="number"
                  name="grados_prisma_oi"
                  value={datosAnteojo.grados_prisma_oi}
                  onChange={handleAnteojoChange}
                  step="0.25"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Base OD:
                <input
                  type="text"
                  name="base_od"
                  value={datosAnteojo.base_od}
                  onChange={handleAnteojoChange}
                  placeholder="Ej: Interna, Externa, Superior"
                />
              </label>
              <label>
                Base OI:
                <input
                  type="text"
                  name="base_oi"
                  value={datosAnteojo.base_oi}
                  onChange={handleAnteojoChange}
                  placeholder="Ej: Interna, Externa, Superior"
                />
              </label>
            </div>
          </CollapsibleSection>

          {/* Información de pago */}
          <div className="form-section">
            <h3>Información de Pago</h3>
            <div className="form-row">
              <label>
                Precio Total:
                <input
                  type="number"
                  name="precio_total"
                  value={datosAnteojo.precio_total}
                  onChange={handleAnteojoChange}
                  step="0.01"
                  required
                />
              </label>
              <label>
                Seña:
                <input
                  type="number"
                  name="sena"
                  value={datosAnteojo.sena}
                  onChange={handleAnteojoChange}
                  step="0.01"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Forma de Pago:
                <select
                  name="tipo_pago"
                  value={datosAnteojo.tipo_pago || 'efectivo'}
                  onChange={handleAnteojoChange}
                  style={{ marginLeft: '8px' }}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta_credito">Tarjeta Crédito</option>
                  <option value="tarjeta_debito">Tarjeta Débito</option>
                </select>
                <span style={{ 
                  display: 'inline-block',
                  marginLeft: '10px', 
                  fontSize: '0.82rem', 
                  color: (datosAnteojo.tipo_pago === 'efectivo' || datosAnteojo.tipo_pago === 'transferencia') ? '#059669' : '#4b5563',
                  fontWeight: 500
                }}>
                  {(datosAnteojo.tipo_pago === 'efectivo' || datosAnteojo.tipo_pago === 'transferencia')
                    ? '• Aplica valor efectivo'
                    : '• Aplica precio de lista'}
                </span>
              </label>
              <div className="saldo-display">
                Saldo: {formatearMoneda(saldoCalculado)}
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div className="form-section">
            <h3>Observaciones</h3>
            <textarea
              name="observaciones"
              value={datosAnteojo.observaciones}
              onChange={handleAnteojoChange}
              placeholder="Observaciones adicionales..."
              rows="3"
            />
          </div>

          <div className="form-actions">
            {!modoAgregarAnteojo && (
              <button type="button" onClick={retrocederAEtapa1} className="btn-secondary">
                ← Volver al Paso 1
              </button>
            )}
            {onClose && (
              <button type="button" onClick={onClose} className="btn-secondary ml-2">
                Cancelar
              </button>
            )}
            <button type="submit" className="btn-primary">
              Finalizar y Guardar
            </button>
          </div>
        </form>
      )}
      
      {mostrarEscanerFicha && (
        <EscanearFicha
          onDatosExtraidos={manejarDatosEscaneados}
          onCerrar={() => setMostrarEscanerFicha(false)}
        />
      )}

        </div>
      </div>
    </div>
  );
};

export default ClienteForm;
